/**
 * LoreCodex.tsx
 * 
 * Displays player's discovered lore revelations
 * Shows all campaigns they've completed and the lore they unlocked
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import './LoreCodex.css';

interface LoreCodexEntry {
  campaignId: string;
  type: 'lore' | 'faction';
  revelationSize: 'minor' | 'moderate' | 'major' | null;
  title: string;
  summary: string;
  wasFirstDiscoverer: boolean;
  completedAt: Date;
  loreDocumentPath: string;
  revelationText: string;
}

interface LoreCodexProps {
  userId: string;
  onClose: () => void;
}

export const LoreCodex: React.FC<LoreCodexProps> = ({ userId, onClose }) => {
  const [entries, setEntries] = useState<LoreCodexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<LoreCodexEntry | null>(null);
  const [filter, setFilter] = useState<'all' | 'lore' | 'faction'>('all');

  useEffect(() => {
    loadCodex();
  }, [userId]);

  const loadCodex = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const codexRef = collection(db, 'players', userId, 'loreCodex');
      const q = query(codexRef, orderBy('completedAt', 'desc'));
      const snapshot = await getDocs(q);

      const loadedEntries: LoreCodexEntry[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        loadedEntries.push({
          campaignId: data.campaignId,
          type: data.type,
          revelationSize: data.revelationSize,
          title: data.title,
          summary: data.summary,
          wasFirstDiscoverer: data.wasFirstDiscoverer,
          completedAt: data.completedAt.toDate(),
          loreDocumentPath: data.loreDocumentPath,
          revelationText: data.revelationText,
        });
      });

      setEntries(loadedEntries);
    } catch (error) {
      console.error('Error loading lore codex:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (filter === 'all') return true;
    return entry.type === filter;
  });

  const getRevelationIcon = (size: string | null) => {
    if (!size) return '📜';
    switch (size) {
      case 'minor': return '📄';
      case 'moderate': return '📜';
      case 'major': return '📕';
      default: return '📜';
    }
  };

  return ReactDOM.createPortal(
    <div className="lore-codex-overlay" onClick={onClose}>
      <div className="lore-codex-content" onClick={e => e.stopPropagation()}>
        <div className="lore-codex-header">
          <h2>Lore Codex</h2>
          <button className="lore-codex-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="lore-codex-loading">Loading your discoveries...</div>
        ) : entries.length === 0 ? (
          <div className="lore-codex-empty">
            <p>Your Lore Codex is empty.</p>
            <p>Complete campaigns to unlock revelations about the world's hidden truths.</p>
          </div>
        ) : (
          <>
            <div className="lore-codex-filters">
              <button
                className={filter === 'all' ? 'active' : ''}
                onClick={() => setFilter('all')}
              >
                All ({entries.length})
              </button>
              <button
                className={filter === 'lore' ? 'active' : ''}
                onClick={() => setFilter('lore')}
              >
                Core Lore ({entries.filter(e => e.type === 'lore').length})
              </button>
              <button
                className={filter === 'faction' ? 'active' : ''}
                onClick={() => setFilter('faction')}
              >
                Faction Secrets ({entries.filter(e => e.type === 'faction').length})
              </button>
            </div>

            {selectedEntry ? (
              <div className="lore-codex-detail">
                <button
                  className="lore-codex-back"
                  onClick={() => setSelectedEntry(null)}
                >
                  ← Back to List
                </button>

                <div className="lore-codex-detail-header">
                  <h3>
                    {getRevelationIcon(selectedEntry.revelationSize)} {selectedEntry.title}
                  </h3>
                  {selectedEntry.wasFirstDiscoverer && (
                    <span className="first-discoverer-badge">⭐ First Discoverer</span>
                  )}
                </div>

                <div className="lore-codex-detail-meta">
                  <span>Type: {selectedEntry.type === 'lore' ? 'Core Lore' : 'Faction Secret'}</span>
                  {selectedEntry.revelationSize && (
                    <span>Size: {selectedEntry.revelationSize}</span>
                  )}
                  <span>Discovered: {selectedEntry.completedAt.toLocaleDateString()}</span>
                </div>

                <div className="lore-codex-detail-summary">
                  <h4>Campaign Synopsis</h4>
                  <p>{selectedEntry.summary}</p>
                </div>

                <div className="lore-codex-detail-revelation">
                  <h4>Revelation</h4>
                  <p>{selectedEntry.revelationText}</p>
                </div>

                <div className="lore-codex-detail-reference">
                  <small>Reference: {selectedEntry.loreDocumentPath}</small>
                </div>
              </div>
            ) : (
              <div className="lore-codex-list">
                {filteredEntries.map(entry => (
                  <div
                    key={entry.campaignId}
                    className="lore-codex-entry"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <div className="lore-codex-entry-icon">
                      {getRevelationIcon(entry.revelationSize)}
                    </div>
                    <div className="lore-codex-entry-content">
                      <h4>
                        {entry.title}
                        {entry.wasFirstDiscoverer && <span className="first-discoverer-icon">⭐</span>}
                      </h4>
                      <p className="lore-codex-entry-summary">{entry.summary}</p>
                      <div className="lore-codex-entry-meta">
                        <span className={`lore-type-badge ${entry.type}`}>
                          {entry.type === 'lore' ? 'Core Lore' : 'Faction Secret'}
                        </span>
                        <span className="lore-codex-entry-date">
                          {entry.completedAt.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

